import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Modal } from 'flowbite';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import EvaluationTemplateScorersTab from './EvaluationTemplateScorersTab';
import EvaluationTemplateUpdateForm from './EvaluationTemplateUpdateForm';
import LineItemRow from './LineItemRow';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';

import PlusCircleIcon from 'Assets/icons/plus-circle.svg';

const TENDERERS = 'tenderers';
const TEMPLATE = 'template';
const EVALUATORS = 'evaluators';

export default function EvaluationTemplateEdit(props) {
    const [itemGroups, setItemGroups] = useState({});
    const [itemGroupsWeighting, setItemGroupsWeighting] = useState({});
    const [showItemsTab, setShowItemsTab] = useState(true);
    const [template, setTemplate] = useState({});
    const [items, setItems] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [users, setUsers] = useState([]);
    const [scorers, setScorers] = useState([]);
    const [scorersLoaded, setScorersLoaded] = useState(false);
    const [showItem, setShowItem] = useState([]);
    const [tableHeaders, setTableHeaders] = useState('items'); // categories, companies
    const [latestResponse, setLatestResponse] = useState({});
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [request, setRequest] = useState(false);

    const editableTable = props.editable === 'true';
    const itemsListRef = useRef();
    const tabContentRef = useRef();
    const divStyle = { overflow: 'auto', maxWidth: '100%', maxHeight: '500px', height: '500px' };
    const [maxTotalWeighting] = useState(100);
    const [availableWeighting, setAvailableWeighting] = useState(100);
    const [usedWeighting, setUsedWeighting] = useState(0);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);
    const [evaluationTemplateDeleteModalElement, setEvaluationTemplateDeleteModalElement] = useState(null);
    const evaluationTemplateDeleteModalRef = useRef();

    const [activeTab, setActiveTab] = useState(TENDERERS);

    const [cells, setCells] = useState({
        show: false,
        sL: 0, // current sroll position
        sT: 0,
        cellW: parseInt(props.cellW), // cell dimensions
        cellH: parseInt(props.cellH),
        dividerW: parseInt(props.dividerW), // divider dimensions
        dividerH: parseInt(props.dividerH),
        hCells: 10, //number of cells horizontally
        vCells: 10, // vertically
    });

    const cleanEvaluationTemplateDeleteModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);
    const hideModalElement = (modalElement) => {
        modalElement && modalElement.hide();
    };
    const successHandlerDelete = () => {
        hideModalElement(evaluationTemplateDeleteModalElement);
        window.location.href = props.redirectUrl;
    };
    const triggerDeleteOfficial = () => {
        setActionInProgress(true);
        setRequest(true);
        axios
            .delete(props.deleteUrl)
            .then((res) => {
                setRequest(false);
                setActionCompleted(true);
            })
            .catch((error) => {
                setRequest(false);
                setActionFailed(true);
            });
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (cells.show) {
            if (TEMPLATE === activeTab) {
                if (itemsListRef.current) {
                    itemsListRef.current.scrollTop = itemsListRef.current.scrollHeight;
                }
            }
        }
        calculateWeighting();
        calculateWeightingBySection();
    }, [items.length]);

    useEffect(() => {
        if (Object.prototype.hasOwnProperty.call(latestResponse, 'data')) {
            let initialTotals = { grand: 0 };

            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'companies')) {
                setCompanies(latestResponse.data.companies);
                initialTotals.companies = {};
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'items')) {
                setItems(latestResponse.data.items);
                initialTotals.items = [];

                const showed = [];
                const groups = {};

                for (let i = 0; i < latestResponse.data.items.length; i++) {
                    const item = latestResponse.data.items[i];
                    showed[i] = true;
                    if (item.type === 'section') {
                        groups[item.id] = true;
                    }
                }
                setItemGroups(groups);
                setShowItem(showed);
                calculateWeighting();
                calculateWeightingBySection();
            }

            setCells((oldState) => {
                var cellNoW; // number of cells on horizontal (width)
                var cellNoH; // number of celss on veritcal (height)
                if ('companies' === tableHeaders) {
                    // companies horizontal
                    cellNoW = Object.prototype.hasOwnProperty.call(latestResponse.data, 'companies') ? latestResponse.data.companies.length + 1 : 1;
                    cellNoH = Object.prototype.hasOwnProperty.call(latestResponse.data, 'items') ? latestResponse.data.items.length + 1 : 1;
                } else if ('items' === tableHeaders) {
                    // items horizontal
                    cellNoH = Object.prototype.hasOwnProperty.call(latestResponse.data, 'companies') ? latestResponse.data.companies.length + 1 : 1;
                    cellNoW = Object.prototype.hasOwnProperty.call(latestResponse.data, 'items') ? latestResponse.data.items.length + 1 : 1;
                }
                var tblH = cellNoH * oldState.cellH;
                var tblW = cellNoW * oldState.cellW;
                return {
                    ...oldState,
                    show: true,
                    tblW: tblW,
                    tblH: tblH,
                    horizontalStart: 0,
                    verticalStart: 0,
                };
            });
        }
        setIsLoaded(true);
    }, [latestResponse]);

    useEffect(() => {
        if (template.id) {
            setEvaluationTemplateDeleteModalElement(new Modal(evaluationTemplateDeleteModalRef.current, modalOptions(false)));
        }
    }, [template]);

    const handleAddItem = useCallback((type) => {
        setItems((oldItems) => {
            let newItems = [...oldItems];
            const item = {
                id: 'new-' + (Math.random() + 1).toString(36).substring(7),
                name: 'item' === type ? $t('New line item') : $t('Heading'),
                weighting: 0,
                type: type,
            };

            newItems.push(item);
            addNewGroupFromItems(item);
            const showed = [...showItem];
            showed.push(true);
            setShowItem(showed);

            return newItems;
        });
        calculateWeighting();
        calculateWeightingBySection();

        setCells((oldState) => {
            return { ...oldState, tblH: oldState.tblH + oldState.cellH };
        });
    });

    const handleSaveTemplate = useCallback((source) => {
        setRequest(true);
        if (availableWeighting > 0) {
            alert($t('The total weighting for all categories must be 100%'));
            if (source === 'evaluators') {
                return Promise.reject();
            }
            return;
        }
        if (source !== 'evaluators') {
            setIsLoaded(false);
        }

        const itemsToSave = {};
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (Object.prototype.hasOwnProperty.call(item, 'deleted') && item.deleted) {
                continue;
            }
            let newId = item.id && item.id.toString().indexOf('new-') === -1 ? item.id : 'new-' + i;

            itemsToSave[newId] = {};
            itemsToSave[newId].type = item.type;
            itemsToSave[newId].order = i != item.order ? i : item.order;

            itemsToSave[newId].name = item.name;
            itemsToSave[newId].weighting = item.weighting;
            if (Object.prototype.hasOwnProperty.call(item, 'deleted') && item.deleted) {
                itemsToSave[newId].deleted = true;
            }
        }

        const data = { items: itemsToSave };
        axios
            .put(props.updateUrl, data)
            .then((response) => {
                fetchData();
            })
            .catch((e) => {
                console.log(e.response);
            })
            .finally(() => {
                setRequest(false);
                if (source !== 'evaluators') {
                    setIsLoaded(true);
                }
                if (source === 'evaluators') {
                    return Promise.resolve();
                }
            });
    });

    function handleOnDragEnd(result) {
        if (!result.destination) {
            return;
        }

        setItems((items) => {
            const newItems = [...items];
            const [reorderedItems] = newItems.splice(result.source.index, 1);
            newItems.splice(result.destination.index, 0, reorderedItems);
            calculateWeightingBySection(newItems);
            return newItems;
        });
    }

    const toggleVisibility = (item, show = false) => {
        if (item.type === 'section') {
            let startIndex = -1;
            let endIndex = -1;

            for (let i = 0; i < items.length; i++) {
                const exp = items[i];
                if (startIndex === -1) {
                    startIndex = exp.id === item.id ? i : startIndex;
                } else if (endIndex === -1) {
                    if (exp.type === 'section' && !exp.deleted) {
                        endIndex = i;
                        break;
                    }
                }
            }

            endIndex = endIndex === -1 ? items.length : endIndex;
            const showed = [...showItem];

            const initVal = showItem[startIndex + 1];
            const val = show || !initVal;

            for (let i = 0; i < showed.length; i++) {
                if (i > startIndex && i < endIndex) {
                    showed[i] = val;
                }
            }

            setShowItem(showed);
        }
    };

    const fetchData = () => {
        axios
            .untranslated()
            .get(props.fetchUrl)
            .then((response) => {
                setLatestResponse(response);
                const itemList = [...response.data.items];
                const companyList = [...response.data.companies];
                const templ = { ...response.data.template };
                const userList = [...response.data.users];
                const scorerList = [...response.data.scorers];

                setItems(itemList);
                setCompanies(companyList);
                setTemplate(templ);
                setUsers(userList);
                setScorers(scorerList);
                setTimeout(() => {
                    setScorersLoaded(true);
                }, 0);
            })
            .catch((e) => {
                console.log(e.response);
            });
    };

    const handleScroll = (e) => {
        setCells((oldState) => {
            var sL = tabContentRef.current.scrollLeft;
            var sT = tabContentRef.current.scrollTop;

            var horizontalStart = Math.floor(sL / oldState.cellW);
            var horizontalRest = sL % oldState.cellW;
            var verticalStart = Math.floor(sT / oldState.cellH);
            var verticalRest = sT % oldState.cellH;
            return {
                ...oldState,
                sT: sT,
                sL: sL,
                horizontalRest: horizontalRest,
                horizontalStart: horizontalStart,
                verticalRest: verticalRest,
                verticalStart: verticalStart,
            };
        });
    };

    useEffect(() => {
        if (tabContentRef.current) {
            setCells((oldState) => {
                let cellNoW; // number of cells on horizontal (width)
                let cellNoH; // number of celss on veritcal (height)
                if ('companies' === tableHeaders) {
                    // companies horizontal
                    cellNoW = companies.length ? companies.length + 1 : 1;
                    cellNoH = items.length ? items.length + 1 : 1;
                } else if ('categories' === tableHeaders) {
                    // items horizontal
                    cellNoH = companies.length ? companies.length + 1 : 1;
                    cellNoW = items.length ? items.length + 1 : 1;
                }
                const tblH = cellNoH * oldState.cellH;
                const tblW = cellNoW * oldState.cellW;
                return {
                    ...oldState,
                    tblW: tblW,
                    tblH: tblH,
                    horizontalStart: 0,
                    verticalStart: 0,
                };
            });
            tabContentRef.current.scrollTop = 0;
            tabContentRef.current.scrollLeft = 0;
        }
    }, [tableHeaders]);

    const handleDeleteItem = useCallback((key) => {
        const deletedItem = items[key];
        if (deletedItem.type === 'section') {
            toggleVisibility(deletedItem, true);
            const groups = { ...itemGroups };
            delete groups[deletedItem.id];
            setItemGroups(groups);
        }

        setItems((oldItems) => {
            let newItems = [...oldItems];
            newItems[key].deleted = true;
            calculateWeighting(newItems);
            calculateWeightingBySection(newItems);

            return newItems;
        });
        setCells((oldState) => {
            return { ...oldState, tblH: oldState.tblH - oldState.cellH };
        });
    });

    const addNewGroupFromItems = (item) => {
        if (item.type === 'section' && !(item.id in itemGroups)) {
            const groups = { ...itemGroups };
            groups[item.id] = true;
            setItemGroups(groups);
            calculateWeightingBySection();
        }
    };

    const updateItem = useCallback((value, arrayKey, propName) => {
        let newValue = value;
        setItems((items) => {
            let newItems = [...items];
            const oldItems = JSON.parse(JSON.stringify(items));
            newItems[arrayKey][propName] = newValue;
            if (propName === 'weighting') {
                let useWeighting = 0;
                newItems.forEach((item) => {
                    if (item.type === 'item' && !item.deleted) {
                        useWeighting += item.weighting;
                    }
                });

                if (useWeighting > maxTotalWeighting) {
                    alert('The total weighting of all line items must not exceed 100%!');
                    calculateWeightingBySection(oldItems);

                    return oldItems;
                } else {
                    setAvailableWeighting(maxTotalWeighting - useWeighting);
                    setUsedWeighting(useWeighting);
                    calculateWeightingBySection(newItems);

                    return newItems;
                }
            }

            return newItems;
        });
    }, []);

    const calculateWeighting = (itemsList = null) => {
        let useWeighting = 0;
        const list = itemsList || items;

        list.forEach((item) => {
            if (item.type === 'item' && !item.deleted) {
                useWeighting += parseInt(item.weighting);
            }
        });

        setAvailableWeighting(maxTotalWeighting - useWeighting);
        setUsedWeighting(useWeighting);
    };

    const calculateWeightingBySection = (itemsList = null) => {
        const groupsWeighting = {};
        let sectionId = null;

        const list = itemsList || items;

        list.forEach((item) => {
            if (item.type === 'section') {
                sectionId = item.id;
                groupsWeighting[sectionId] = 0;
            } else {
                if (sectionId && !item.deleted) {
                    groupsWeighting[sectionId] += parseInt(item.weighting);
                }
            }
        });

        setItemGroupsWeighting(groupsWeighting);
    };

    const deleteEvaluationTemplate = () => {
        evaluationTemplateDeleteModalElement && evaluationTemplateDeleteModalElement.show();
    };

    return (
        <>
            <div className="tw-flex tw-flex-col tw-gap-6">
                <div
                    className="in-nav horizontal"
                    data-tabs-active-classes="active"
                    data-tabs-inactive-classes="inactive"
                    data-default-tab-id="tenderers-tab"
                    data-tabs-toggle="#evaluation-template-tab-content"
                    role="tablist"
                >
                    <button
                        className="in-nav__item"
                        id="tenderers-tab"
                        data-tabs-target="#tenderers"
                        type="button"
                        role="tab"
                        aria-controls="tenderers"
                        aria-selected="true"
                        onClick={() => {
                            setActiveTab(TENDERERS);
                        }}
                    >
                        {$t('Tenderers')}
                    </button>
                    <button
                        className="in-nav__item"
                        id="evaluation-template-page-tab"
                        data-tabs-target="#evaluation-template-page"
                        type="button"
                        role="tab"
                        aria-controls="evaluation-template-page"
                        aria-selected="false"
                        onClick={() => {
                            setActiveTab(TEMPLATE);
                        }}
                    >
                        {$t('Evaluation Template')}
                    </button>
                    <button
                        className="in-nav__item"
                        id="evaluators-tab"
                        data-tabs-target="#evaluators"
                        type="button"
                        role="tab"
                        aria-controls="evaluators"
                        aria-selected="false"
                        onClick={() => {
                            setActiveTab(EVALUATORS);
                        }}
                    >
                        {$t('Evaluators')}
                    </button>

                    {editableTable && TEMPLATE === activeTab && parseInt(template.status) === 0 && (
                        <li className={'tw-w-full tw-flex tw-justify-end tw-gap-1'}>
                            <Button variant="danger" data-dismiss="modal" onClick={deleteEvaluationTemplate}>
                                {$t('Delete')}
                            </Button>
                            <Button variant="primary" type="button" onClick={handleSaveTemplate} disabled={request}>
                                {$t('Save')}
                            </Button>
                        </li>
                    )}
                </div>

                <div id="evaluation-template-tab-content">
                    <div id="tenderers" role="tabpanel" aria-labelledby="tenderers-tab">
                        {isLoaded ? (
                            <EvaluationTemplateUpdateForm companiesIndexUrl={companiesIndexUrl} companiesUpdateUrl={companiesUpdateUrl} />
                        ) : (
                            <div>Loading...</div>
                        )}
                    </div>
                    <div id="evaluation-template-page" role="tabpanel" aria-labelledby="evaluation-template-page-tab">
                        {isLoaded ? (
                            <div className="d-flex flex-column h-100 mh-100">
                                {editableTable && (
                                    <div className="row pb-2">
                                        <div className="col">
                                            <Button small variant="primary" className="mr-2" type="button" onClick={(e) => handleAddItem('section')}>
                                                <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2" />
                                                {$t('Add heading')}
                                            </Button>
                                            <Button small variant="primary" type="button" onClick={(e) => handleAddItem('item')}>
                                                <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2" />
                                                {$t('Add line item')}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                <div className="tw-overflow-y-auto tw-overflow-x-auto tw-border-2 tw-border-grey tw-rounded-t-lg tw-text-charcoal tw-text-sm">
                                    <div className="tw-sticky tw-top-0 tw-flex tw-items-end tw-font-semibold tw-bg-blue-25">
                                        <div className="tw-basis-12 tw-shrink-0 tw-p-3"></div>
                                        <div className="tw-basis-full tw-flex tw-items-end">
                                            <div className="tw-basis-6/12 tw-p-3">{$t('Name')}</div>
                                            <div className="tw-basis-5/12 tw-p-3">
                                                {$t('Weighting')} ({usedWeighting} / {maxTotalWeighting})
                                            </div>
                                            {editableTable && <div className="tw-basis-1/12 tw-p-3">{$t('Delete')}</div>}
                                        </div>
                                        <div className="tw-basis-12 tw-shrink-0 tw-p-3"></div>
                                    </div>
                                </div>

                                <div ref={itemsListRef} className={'tw-border-x-2 tw-border-b-2 tw-rounded-b-lg tw-border-grey'}>
                                    <DragDropContext onDragEnd={handleOnDragEnd}>
                                        <Droppable droppableId="itemsList">
                                            {(provided) => (
                                                <div className="itemsList" {...provided.droppableProps} ref={provided.innerRef}>
                                                    {items.map((item, i) => {
                                                        if (!item.deleted) {
                                                            if (editableTable) {
                                                                return (
                                                                    <Draggable key={i} draggableId={i.toString()} index={i}>
                                                                        {(provided) => (
                                                                            <div
                                                                                ref={provided.innerRef}
                                                                                {...provided.draggableProps}
                                                                                {...provided.dragHandleProps}
                                                                                className={showItem[i] ? '' : 'hidden'}
                                                                            >
                                                                                <LineItemRow
                                                                                    updateItem={updateItem}
                                                                                    weighting={item.weighting}
                                                                                    name={item.name}
                                                                                    type={item.type}
                                                                                    itemKey={i}
                                                                                    itemId={item.id}
                                                                                    itemGroupsWeighting={itemGroupsWeighting}
                                                                                    handleDeleteItem={handleDeleteItem}
                                                                                    handleClick={() => toggleVisibility(item)}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                );
                                                            } else {
                                                                return <div key={i}>{$t('ExpenseItemShow')}</div>;
                                                            }
                                                        }
                                                    })}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </div>
                            </div>
                        ) : (
                            <div>Loading...</div>
                        )}
                    </div>
                    <div id="evaluators" role="tabpanel" aria-labelledby="evaluators-tab">
                        {isLoaded && scorersLoaded ? (
                            <EvaluationTemplateScorersTab
                                scorers={scorers}
                                users={users}
                                storeScorersUrl={props.storeScorersUrl}
                                fetchData={fetchData}
                                template={template}
                                listUsersUrl={props.listUsersUrl}
                                redirectUrl={props.redirectUrl}
                                handleSaveTemplate={() => handleSaveTemplate('evaluators')}
                            />
                        ) : (
                            <div>Loading...</div>
                        )}
                    </div>
                </div>
            </div>
            <ConfirmationModal
                closeHandler={() => {}}
                successHandler={successHandlerDelete}
                triggerAction={triggerDeleteOfficial}
                cleanStates={cleanEvaluationTemplateDeleteModalStates}
                allowAction={true}
                modalId={`delete-evaluation-template`}
                modalRef={evaluationTemplateDeleteModalRef}
                modalElement={evaluationTemplateDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: template.name })}
                actionCompletedMsg={$t('{{name}} deleted', { name: template.name })}
                actionFailedMsg={$t('{{name}} could not be deleted', { name: template.name })}
                title={$t('Delete Evaluation Template')}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />
        </>
    );
}

if (document.getElementById('EvaluationTemplateEdit')) {
    const rootEl = document.getElementById('EvaluationTemplateEdit');
    ReactDOM.render(
        <App>
            <EvaluationTemplateEdit
                editable={rootEl.getAttribute('data-editable')}
                cellW={rootEl.getAttribute('data-cellW')}
                cellH={rootEl.getAttribute('data-cellH')}
                dividerW={rootEl.getAttribute('data-dividerW')}
                dividerH={rootEl.getAttribute('data-dividerH')}
                fetchUrl={fetchUrl}
                updateUrl={updateUrl}
                storeScorersUrl={storeScorersUrl}
                listUsersUrl={listUsersUrl}
                deleteUrl={deleteUrl}
                redirectUrl={redirectUrl}
            />
        </App>,
        rootEl,
    );
}
